// Generic imports
import dayjs from 'dayjs';
import { validitySentence } from '../../utils/date.js';
import _ from 'lodash';

// Project imports
import { calcOfferSum, calcFieldChargeLineValue } from '../../utils/opportunity.js';
import { getDisplayRate } from '../utils/opportunity.js';
import currencyToText from '../../CurrencyToText';
import { FieldTypes } from '../../FieldTypes.js';
import { getTotalRates } from '../utils/rates.js';


// Email clients give us no stylesheet: Gmail strips <style> blocks and nothing loads
// App.css, so whatever must survive the send has to carry its own inline style. Pasted
// tables and screenshots are exactly that - on screen they are styled by a rule that
// never reaches the recipient.
function addInlineStyle(html, tagPattern, css) {
    return (html || '').replace(new RegExp(`<(${tagPattern})(\\s[^>]*)?>`, 'gi'), (match, tag, attrs) => {
        attrs = attrs || '';
        const existing = attrs.match(/\sstyle\s*=\s*"([^"]*)"/i);
        // Ours go first so anything already on the element still wins.
        if (existing) return `<${tag}${attrs.replace(existing[0], ` style="${css}${existing[1]}"`)}>`;
        return `<${tag}${attrs} style="${css}">`;
    });
}

const CELL_STYLE = 'border:1px solid #d0d5dd;padding:4px 8px;vertical-align:top;';

function inlineStylesForEmail(html) {
    let out = addInlineStyle(html, 'table', 'border-collapse:collapse;margin:8px 0;');
    out = addInlineStyle(out, 'td', CELL_STYLE);
    out = addInlineStyle(out, 'th', `${CELL_STYLE}background:#f7f8fa;font-weight:600;text-align:left;`);
    // A screenshot arrives at whatever size it was captured; unconstrained it blows the
    // email width apart.
    out = addInlineStyle(out, 'img', 'max-width:100%;height:auto;');
    return out;
}

function offerToText({ type, offers, preface, epilogue, products, transportationMethod, buyingRates, exchangeRates, expanded, terms, link, lockedExchangeRates, organization }) {
    const parts = [];

    if (type === 'one-off-free-text') {
        preface = (preface || '').replace(/<span[^>]*data-field[^>]*>(.*?)<\/span>/g, '$1').replace(/<span[^>]*data-total[^>]*>(.*?)<\/span>/g, '$1');
        preface = (preface || '').replace(/<div><\/div>/g, '<br/>');
        // A <table> inside <pre> is invalid - mail clients each recover from it differently.
        // A div with the same white-space rule wraps identically and nests legally.
        parts.push('<div style="font-family: Poppins, sans-serif; font-size: 12px; white-space: pre-wrap;">');
        parts.push(preface);
        parts.push(epilogue);
        if (!_.isEmpty(terms)) {
            parts.push('');
            terms.forEach(term => {
                parts.push(term.value || '');
            });
        }
        parts.push('</div>');
    } else if (type === 'one-off-email') {
        // Same reason as the free-text branch: this builder shares the editor, so its body
        // can hold a pasted table, and a table inside <pre> is invalid.
        parts.push('<div style="font-family: inherit; font-size: inherit; white-space: pre-wrap;">' + (preface ? preface : ''));
        if (preface) {
            parts.push('');
        }

        offers.forEach(offer => {
            const total = calcOfferSum({ offer, products, transportationMethod, buyingRates, exchangeRates, lockedExchangeRates, organization });

            if (offer.displayCurrency) {
                total.max.sum = {
                    [offer.displayCurrency]: _.sum(_.map(total.max.sum, (value, currency) => {
                        const rate = getDisplayRate({
                            sourceCurrency: currency,
                            displayCurrency: offer.displayCurrency,
                            displayCurrencySource: offer.displayCurrencySource,
                            lockedExchangeRates,
                            liveRates: exchangeRates,
                            fixedRates: organization.exchangeRates,
                            organization
                        });
                        return value * rate;
                    }))
                }
                total.min.sum = {
                    [offer.displayCurrency]: _.sum(_.map(total.min.sum, (value, currency) => {
                        const rate = getDisplayRate({
                            sourceCurrency: currency,
                            displayCurrency: offer.displayCurrency,
                            displayCurrencySource: offer.displayCurrencySource,
                            lockedExchangeRates,
                            liveRates: exchangeRates,
                            fixedRates: organization.exchangeRates,
                            organization
                        });
                        return value * rate;
                    }))
                }
            }
            const totalStr = _.map(total.max.sum, (value, currency) => currencyToText(currency, true, value.toLocaleString({ maximumFractionDigits: 2 }))).join(' + ');

            parts.push('<b>' + offer.title + '</b> - ' + totalStr);
            if (offer.description) {
                parts.push('<i>' + offer.description + '</i>');
            }

            offer.sections.forEach((section) => {
                let first = true;
                section.offers.forEach((sectionOffer) => {
                    sectionOffer.fields.forEach((field) => {
                        if (!expanded && field.type !== 'string') return;
                        if (first) {
                            if (section.title) parts.push('  ' + section.title);
                            first = false;
                        }
                        if (field.title) parts.push(`  ${section.title ? '  ' : ''}` + field.title);
                        parts.push(getFieldValue({ field, section, products, transportationMethod, buyingRates, sections: offer.sections, exchangeRates, lockedExchangeRates, organization }));
                    })
                })
            })
            if (expanded) {
            }
            if (offer.transitTime) {
                const v = offer.transitTime;
                const mins = parseInt((v % 1) * 60);
                const days = parseInt(parseInt(v) / 24);
                const hours = parseInt(v) % 24;
                const str = [
                    days ? (days === 1 ? `${days} day` : `${days} days`) : '',
                    hours ? (hours === 1 ? `${hours} hour` : `${hours} hours`) : '',
                    mins ? (mins === 1 ? `${mins} min` : `${mins} mins`) : '',
                ].join(' ');
                parts.push('Transit Time: ' + str);
            }
            if (offer.validUntil || offer.validFrom) {
                parts.push(validitySentence(offer, d => dayjs(d).format('L')));
            }
            parts.push('');
        });

        if (link) {
            const linkText = getAcceptQuoteLinkText({ organization, link });
            parts.push(linkText);
            parts.push('');
        }

        parts.push(epilogue);

        if (!_.isEmpty(terms)) {
            parts.push('');
            terms.forEach(term => {
                parts.push(term.value);
            });
        }
        parts.push('</div>');
    } else if (type === 'one-off') {
        parts.push('<div style="font-family: \'Poppins\', sans-serif; font-size: inherit;">' + (preface ? preface : ''));
        if (preface) {
            parts.push('');
        }

        if (link) {
            const linkText = getAcceptQuoteLinkText({ organization, link });
            parts.push(linkText);
            parts.push('');
        }

        parts.push(epilogue);
        if (!_.isEmpty(terms)) {
            parts.push('');
            terms.forEach(term => {
                parts.push(term.value);
            });
        }
        parts.push('</div>');
    }

    return inlineStylesForEmail(parts.join('<br/>'));
}

export { offerToText };

export function getAcceptQuoteLinkText({ organization, link }) {
    if (!link) return '';
    const defaultString = _.get(organization, 'defaults.defaultStrings.acceptQuoteLink', 'Click {here} to accept this quote, and we\'ll get started on moving your cargo!');
    return defaultString.replace(/\{([^}]+)\}/g, (match, linkText) => `<a href="${link}">${linkText}</a>`);
}

export function getEmailClosing({ organization }) {
    return _.get(organization, 'defaults.defaultStrings.emailClosing', 'Best Regards');
}

export function getDefaultEmailPrefaceForEmailPage({ organization, link }) {
    const template = _.get(organization, 'defaults.defaultStrings.emailPrefaceForEmailPage', '<div>Hello,</div><div></div><div>[Add your email here]</div><div></div><div>{link}</div><div></div><div>{closing}</div>');
    let result = template;
    
    // Replace {link} placeholder with actual link HTML
    if (link) {
        const linkText = getAcceptQuoteLinkText({ organization, link: link.startsWith('http://') || link.startsWith('https://') ? link : `https://${link}` });
        result = result.replace(/\{link\}/g, linkText);
    } else {
        // If no link, replace with placeholder text
        result = result.replace(/\{link\}/g, 'Click {here} to accept this quote, and we\'ll get started on moving your cargo!');
    }
    
    // Replace {closing} placeholder with actual closing text
    const closingText = getEmailClosing({ organization });
    result = result.replace(/\{closing\}/g, closingText);
    
    return result;
}

export function getDefaultEmailBody({ organization, transportationMethod, link }) {
    const template = _.get(organization, 'defaults.defaultStrings.emailBodyTemplate', 'Hello,<br/><br/>Thank you for your inquiry.<br/><br/>Further to your request, please find hereby our rates for {transportationMethod}.<br/><br/>Please note that rates are subject to availability of space & equipment at {portType} of loading at time of shipment.<br/><br/>{link}<br/><br/>{closing}');
    let result = template;
    
    // Replace transportation method
    const transportationMethodTranslations = _.get(organization, 'defaults.defaultStrings.transportationMethodTranslations', {
        sea: 'ocean cargo',
        air: 'air cargo',
        land: 'land transport'
    });
    const transportationMethodText = transportationMethodTranslations[transportationMethod] || transportationMethod;
    result = result.replace(/\{transportationMethod\}/g, transportationMethodText);
    
    // Replace port type
    const portTypeTranslations = _.get(organization, 'defaults.defaultStrings.portTypeTranslations', {
        sea: 'port',
        air: 'airport',
        land: 'port'
    });
    const portTypeText = portTypeTranslations[transportationMethod] || 'port';
    result = result.replace(/\{portType\}/g, portTypeText);
    
    // Replace {link} placeholder with actual link HTML
    if (link) {
        const linkText = getAcceptQuoteLinkText({ organization, link });
        result = result.replace(/\{link\}/g, linkText);
    } else {
        result = result.replace(/\{link\}/g, '');
    }
    
    // Replace {closing} placeholder with actual closing text
    const closingText = getEmailClosing({ organization });
    result = result.replace(/\{closing\}/g, closingText);
    
    return result;
}

export function getEmailSubject({ organization, opportunity, readableId }) {
    const transportationMethod = opportunity.transportationMethod;
    let template;
    
    if (opportunity.type === 'contract') {
        template = _.get(organization, 'defaults.defaultStrings.emailSubjectTemplateContract', '{transportationMethod} Contract Quotation #{id}');
        const translations = _.get(organization, 'defaults.defaultStrings.subjectTransportationMethodContract', {
            sea: 'Ocean',
            air: 'Air',
            land: 'Land'
        });
        const transportationMethodText = translations[transportationMethod] || transportationMethod;
        return template.replace(/\{transportationMethod\}/g, transportationMethodText).replace(/\{id\}/g, readableId);
    } else {
        const translations = _.get(organization, 'defaults.defaultStrings.subjectTransportationMethodOneOff', {
            sea: 'Ocean Cargo',
            air: 'Air Cargo',
            land: 'Land Transport'
        });
        const transportationMethodText = translations[transportationMethod] || transportationMethod;
        
        if (!_.isEmpty(opportunity.routes) && opportunity.routes[0]) {
            template = _.get(organization, 'defaults.defaultStrings.emailSubjectTemplateWithRoutes', '{transportationMethod} from {source} to {destination} - Quotation #{id}');
            const source = opportunity.routes[0].source?.text || '';
            const destination = opportunity.routes[0].destination?.text || '';
            let result = template
                .replace(/\{transportationMethod\}/g, transportationMethodText)
                .replace(/\{id\}/g, readableId);
            
            // Handle source and destination - remove "from " if source is empty, remove " to " if destination is empty
            if (source) {
                result = result.replace(/\{source\}/g, source);
            } else {
                result = result.replace(/\s*from\s*\{source\}\s*/g, ' ');
            }
            
            if (destination) {
                result = result.replace(/\{destination\}/g, destination);
            } else {
                result = result.replace(/\s*to\s*\{destination\}\s*/g, ' ');
            }
            
            // Clean up any double spaces
            result = result.replace(/\s+/g, ' ').trim();
            
            return result;
        } else {
            template = _.get(organization, 'defaults.defaultStrings.emailSubjectTemplateDefault', '{transportationMethod} - Quotation #{id}');
            return template.replace(/\{transportationMethod\}/g, transportationMethodText).replace(/\{id\}/g, readableId);
        }
    }
}

function getFieldValue({ field, section, products, transportationMethod, buyingRates, sections, exchangeRates, lockedExchangeRates, organization }) {

    if (field.type === 'string') {
        return `  ${section.title ? '  ' : ''}${field.title ? '  ' : ''}${field.values.string?.value || ''}`;
    }

    const fieldType = FieldTypes[transportationMethod].find(f => f.value === field.type);
    const chargeLines = fieldType?.getChargeLines ? fieldType.getChargeLines({ field, products, organization }) : [];
    return _.compact(chargeLines.map(line => {
        const { title, fieldValueId, quantityMultiplier: multiplier, multiplierText } = line;
        const productId = fieldValueId;
        const value = field.values[productId];
        if (!value) return null;

        if (value.formula) {
            const totalBuyingPrice = getTotalRates({ field, fieldBuyingRates: field.buyingRates, products, transportationMethod, buyingRates, exchangeRates, skipSumCurrencies: true, organization });
            const cost = _.sum(_.map(totalBuyingPrice, (val, currency) => val / exchangeRates[currency])) || 0;
            const calcValue = calcFieldChargeLineValue({
                field,
                line,
                sections,
                products,
                transportationMethod,
                exchangeRates,
                organization,
                cost,
                buyingRates
            });

            const [ formulaValue, currency, ov, oc, rate ] = (() => {
                if (!calcValue) return [ 0, value.currency ];
                if (!field.displayCurrency) return [ calcValue.sum[value.currency], value.currency ];

                const rate = getDisplayRate({
                    sourceCurrency: value.currency,
                    displayCurrency: field.displayCurrency,
                    displayCurrencySource: field.displayCurrencySource,
                    lockedExchangeRates,
                    liveRates: exchangeRates,
                    fixedRates: organization.exchangeRates,
                    organization
                });
                return [ calcValue.sum[value.currency] * rate, field.displayCurrency, calcValue.sum[value.currency], value.currency, rate ];
            })();
            return `  ${section.title ? '  ' : ''}${field.title ? '  ' : ''}${currencyToText(currency, true, (formulaValue || 0).toLocaleString({ maximumFractionDigits: 2 }))}${oc ? ` (${currencyToText(oc, true, ov.toLocaleString({ maximumFractionDigits: 2 }))} x ${rate.toLocaleString({ maximumFractionDigits: 2 })} ${currency}/${oc})` : ''} (for ${multiplier.toLocaleString({ maximumFractionDigits: 2 })} ${multiplierText})`;
        }

        const [ v, c, ov, oc, rate ] = (() => {   
            if (!field.displayCurrency) return [ value.value, value.currency ];

            const rate = getDisplayRate({
                sourceCurrency: value.currency,
                displayCurrency: field.displayCurrency,
                displayCurrencySource: field.displayCurrencySource,
                lockedExchangeRates,
                liveRates: exchangeRates,
                fixedRates: organization.exchangeRates,
                organization
            });

            return [ value.value * rate, field.displayCurrency, value.value, value.currency, rate ];
        })();

        return `  ${section.title ? '  ' : ''}${field.title ? '  ' : ''}${title ? `${title}: ` : ''}${currencyToText(c, true, v.toLocaleString({ maximumFractionDigits: 2 }))}${oc ? ` (${currencyToText(oc, true, ov.toLocaleString({ maximumFractionDigits: 2 }))} x ${rate.toLocaleString({ maximumFractionDigits: 2 })} ${c}/${oc})` : ''} x ${multiplier.toLocaleString({ maximumFractionDigits: 2 })} ${multiplierText}`;
    })).join('<br/>');
}
